/**
 * Geometría del editor de plantillas avanzadas (PLAN_SUTEX_Y_PLANTILLAS §3.0bis, §3.9).
 *
 * Funciones puras sobre la definición: lo mismo que valida el backend
 * (`apps/print_templates/esquema.py`), para que el lienzo no deje armar algo
 * que después no se pueda publicar.
 */

export type TipoElemento =
  | 'text' | 'field' | 'field_list' | 'rect' | 'line' | 'detail_table' | 'references_table' | 'timbre' | 'qr'
  | 'image' | 'barcode'
export type Paginas = 'first' | 'all' | 'last'
export type Vista = 'first' | 'next' | 'last'
/** Qué copia lleva el elemento: la original, la cedible (la que va al factoring) o las dos. */
export type Copia = 'original' | 'cedible'

export interface Estilo {
  font_family?: 'sans' | 'serif' | 'mono'
  font_size_pt?: number
  bold?: boolean
  italic?: boolean
  uppercase?: boolean
  align?: 'left' | 'center' | 'right'
  valign?: 'top' | 'middle' | 'bottom'
  color?: string
  background?: string
  border_mm?: number
  border_color?: string
  radius_mm?: number
  padding_mm?: number
  line_height?: number
}

export interface Columna {
  header: string
  /** Un campo de la línea; o `text`, que compone varios: «{NmbItem}\n{DscItem}». */
  path?: string
  text?: string
  width_pct: number
  align?: 'left' | 'center' | 'right'
  formats?: string[]
  /** Solo va en los documentos que la cumplen: `tipo == 61`. Dos columnas alternativas ocupan el mismo lugar. */
  condition?: string
}

/** Una fila de la lista de datos: si el valor queda vacío, la fila no se imprime y las de abajo suben. */
export interface FilaDeLista {
  label: string
  value: string
  /** `false`: se imprime aunque el valor venga vacío. */
  hide_empty?: boolean
}

export interface Grilla {
  width_mm?: number
  color?: string
}

/** Las referencias que van sobre el detalle, solo cuando el documento las trae. */
export interface ReferenciasDelDetalle {
  title?: string
  columns: Columna[]
  row_h_mm?: number
  gap_mm?: number
  header_style?: Estilo
}

export interface Elemento {
  id: string
  type: TipoElemento
  x_mm: number
  y_mm: number
  w_mm: number
  h_mm?: number
  pages?: Paginas
  z?: 'back' | 'front'
  copy?: 'both' | Copia
  condition?: string
  text?: string
  path?: string
  formats?: string[]
  value?: string
  legend?: boolean
  y_next_mm?: number
  bottom_mm?: number
  columns?: Columna[]
  style?: Estilo
  header_style?: Estilo
  opacity?: number
  /** Detalle y lista de datos: las líneas entre columnas. */
  grid?: Grilla | false
  references?: ReferenciasDelDetalle
  // Lista de datos
  rows?: FilaDeLista[]
  label_w_mm?: number
  row_h_mm?: number
  hide_empty?: boolean
  label_style?: Estilo
  value_style?: Estilo
  // Imagen
  asset?: string
  fit?: 'contain' | 'cover' | 'stretch'
  // Código de barras
  symbology?: 'code128' | 'code39'
  show_text?: boolean
  check_digit?: boolean
}

export interface Papel {
  preset: string
  orientation?: 'portrait' | 'landscape'
  width_mm?: number
  height_mm?: number | null
  /** La página dos veces en una hoja del doble de alto: media carta → carta. */
  two_per_sheet?: boolean
}

export interface Fondo {
  asset: string
  /** `first`: solo la primera página; `all`: todas (la 2.ª página del PDF, si la trae, en las siguientes). */
  pages?: 'first' | 'all'
}

export interface Definicion {
  paper: Papel
  font?: { family?: 'sans' | 'serif' | 'mono'; size_pt?: number }
  background?: Fondo
  elements: Elemento[]
}

/** CSS a 96 ppp: 1 mm = 3,7795 px a zoom 100 %. */
export const PX_POR_MM = 96 / 25.4

const PRESETS: Record<string, [number, number | null]> = {
  letter: [215.9, 279.4],
  legal: [215.9, 330.2],
  half_letter: [215.9, 139.7],
  a4: [210, 297],
  roll_80: [80, null],
  roll_76: [76, null],
  roll_58: [58, null],
}

// Manual de muestras impresas del SII: el timbre mide de 2×5 a 4×9 cm, y la
// leyenda suma 8 mm dentro de la caja (esquema.py).
export const TIMBRE = { anchoMin: 50, anchoMax: 90, altoMin: 20, altoMax: 40, leyenda: 8 }

/** Margen bajo el último elemento cuando el alto es automático (rollos). */
const AIRE_ROLLO_MM = 20

export function dimensiones(papel: Papel): { ancho: number; alto: number | null } {
  let ancho: number
  let alto: number | null
  if (papel.preset === 'custom') {
    ancho = papel.width_mm ?? 80
    alto = papel.height_mm ?? null
  } else {
    [ancho, alto] = PRESETS[papel.preset] ?? PRESETS.letter
  }
  if (alto !== null && papel.orientation === 'landscape') [ancho, alto] = [alto, ancho]
  return { ancho, alto }
}

export function fondoDe(el: Elemento): number {
  if (el.type === 'detail_table') return el.bottom_mm ?? el.y_mm
  return el.y_mm + (el.h_mm ?? 0)
}

/** El alto que dibuja el lienzo: el del papel, o el que ocupa el contenido si es automático. */
export function altoDelLienzo(def: Definicion): number {
  const { alto } = dimensiones(def.paper)
  if (alto !== null) return alto
  const fondo = Math.max(60, ...def.elements.map(fondoDe))
  return fondo + AIRE_ROLLO_MM
}

export function visibleEnVista(el: Elemento, vista: Vista): boolean {
  if (el.type === 'detail_table') return true
  const paginas = el.pages ?? 'all'
  return paginas === 'all' || (paginas === 'first' && vista === 'first') || (paginas === 'last' && vista === 'last')
}

/** Dónde dibuja la tabla del detalle cada vista: su zona en la primera página o en las siguientes. */
export function zonaDelDetalle(el: Elemento, vista: Vista): { top: number; bottom: number } {
  const top = vista === 'first' ? el.y_mm : el.y_next_mm ?? el.y_mm
  return { top, bottom: el.bottom_mm ?? top + 20 }
}

export const ajustar = (mm: number, paso = 1) => Math.round(mm / paso) * paso
const entre = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

/**
 * La caja del elemento dentro de la página y con las medidas que el backend
 * acepta: un timbre fuera de las medidas del SII no se podría publicar.
 */
export function acotar(el: Elemento, ancho: number, alto: number | null): Elemento {
  const limiteY = alto ?? 2000
  const c = { ...el }
  if (c.type === 'timbre') {
    const leyenda = c.legend === false ? 0 : TIMBRE.leyenda
    c.w_mm = entre(c.w_mm, TIMBRE.anchoMin, TIMBRE.anchoMax)
    c.h_mm = entre(c.h_mm ?? 30, TIMBRE.altoMin + leyenda, TIMBRE.altoMax + leyenda)
  }
  c.w_mm = entre(c.w_mm, 1, ancho)
  c.x_mm = entre(c.x_mm, 0, ancho - c.w_mm)
  if (c.type === 'detail_table') {
    c.y_mm = entre(c.y_mm, 0, limiteY - 5)
    c.y_next_mm = entre(c.y_next_mm ?? c.y_mm, 0, c.y_mm)
    c.bottom_mm = entre(c.bottom_mm ?? c.y_mm + 20, c.y_mm + 5, limiteY)
  } else {
    const minimo = c.type === 'line' ? 0.1 : 1
    c.h_mm = entre(c.h_mm ?? 5, minimo, limiteY)
    c.y_mm = entre(c.y_mm, 0, limiteY - c.h_mm)
  }
  return c
}

/**
 * Mover el borde superior de la zona del detalle en la vista actual. La zona
 * de las páginas siguientes no puede empezar más abajo que la de la primera
 * (esquema.py): si el usuario lo fuerza, la otra se mueve con ella.
 */
export function moverInicioDelDetalle(el: Elemento, vista: Vista, top: number): Elemento {
  if (vista === 'first') {
    return { ...el, y_mm: top, y_next_mm: Math.min(el.y_next_mm ?? top, top) }
  }
  return { ...el, y_next_mm: top, y_mm: Math.max(el.y_mm, top) }
}

export function nuevoId(tipo: string, existentes: Elemento[]): string {
  const usados = new Set(existentes.map(e => e.id))
  let n = 1
  while (usados.has(`${tipo}-${n}`)) n += 1
  return `${tipo}-${n}`
}

const TAMANO_INICIAL: Record<TipoElemento, [number, number]> = {
  text: [50, 6],
  field: [40, 6],
  rect: [40, 20],
  line: [60, 0.3],
  detail_table: [190, 120],
  references_table: [190, 15],
  timbre: [70, 36],
  qr: [25, 25],
  field_list: [90, 18],
  image: [40, 20],
  barcode: [60, 15],
}

/** Alto de una fila de la lista de datos según su letra: lo que FacturaDesktop deja entre filas. */
export const altoDeFila = (pt: number) => Math.round(pt * 0.526 * 100) / 100

/** Un elemento nuevo soltado en (x, y) mm, con contenido de partida. */
export function elementoNuevo(tipo: TipoElemento, x: number, y: number, def: Definicion): Elemento {
  const { ancho, alto } = dimensiones(def.paper)
  const [w, h] = TAMANO_INICIAL[tipo]
  const base: Elemento = { id: nuevoId(tipo.replace('_', '-'), def.elements), type: tipo, x_mm: x, y_mm: y, w_mm: w }
  let el: Elemento
  switch (tipo) {
    case 'detail_table':
      el = {
        ...base, x_mm: 10, w_mm: Math.min(w, ancho - 20), y_next_mm: y, bottom_mm: y + h,
        columns: [
          { header: 'Descripción', path: 'NmbItem', width_pct: 60 },
          { header: 'Cantidad', path: 'QtyItem', width_pct: 15, align: 'right' },
          { header: 'Total', path: 'MontoItem', width_pct: 25, align: 'right', formats: ['money'] },
        ],
      }
      break
    case 'references_table':
      el = {
        ...base, h_mm: h, w_mm: Math.min(w, ancho - x), pages: 'first',
        columns: [
          { header: 'Tipo', path: 'TpoDocRef', width_pct: 20 },
          { header: 'Folio', path: 'FolioRef', width_pct: 20 },
          { header: 'Fecha', path: 'FchRef', width_pct: 20, formats: ['date'] },
          { header: 'Razón', path: 'RazonRef', width_pct: 40 },
        ],
      }
      break
    case 'timbre':
      el = { ...base, h_mm: h, pages: 'last' }
      break
    case 'qr':
      el = { ...base, h_mm: h, value: '{Encabezado/IdDoc/Folio}' }
      break
    case 'field_list':
      el = conAltoDeLista({
        ...base, h_mm: h, label_w_mm: 25, row_h_mm: altoDeFila(def.font?.size_pt ?? 9), hide_empty: true,
        label_style: { bold: true },
        rows: [
          { label: 'Señor(es):', value: '{Encabezado/Receptor/RznSocRecep}' },
          { label: 'R.U.T.:', value: '{Encabezado/Receptor/RUTRecep|rut}' },
          { label: 'Dirección:', value: '{Encabezado/Receptor/DirRecep}' },
          { label: 'Giro:', value: '{Encabezado/Receptor/GiroRecep}' },
        ],
      })
      break
    case 'image':
      el = { ...base, h_mm: h, fit: 'contain' }
      break
    case 'barcode':
      el = { ...base, h_mm: h, symbology: 'code128', value: '{Encabezado/IdDoc/Folio}', show_text: true }
      break
    case 'rect':
      el = { ...base, h_mm: h, style: { border_mm: 0.3 } }
      break
    case 'line':
      el = { ...base, h_mm: h, style: { border_mm: 0.3 } }
      break
    default:
      el = { ...base, h_mm: h, text: 'Texto' }
  }
  return acotar(el, ancho, alto)
}

/** La lista con el alto justo para sus filas: al agregar una fila no queda cortada. */
export function conAltoDeLista(el: Elemento): Elemento {
  const filas = Math.max(1, el.rows?.length ?? 1)
  return { ...el, h_mm: Math.round(filas * (el.row_h_mm ?? 4.5) * 100) / 100 }
}

// Ley 19.983, art. 4 b) y 5 c): el recuadro que firma quien recibe la mercadería, en la copia cedible.
const LEYENDA_ACUSE = '"El acuse de recibo que se declara en este acto, de acuerdo a lo dispuesto en la letra b) del '
  + 'Art. 4°, y la letra c) del Art. 5° de la Ley 19.983, acredita que la entrega de mercaderías o servicio(s) '
  + 'prestado(s) ha(n) sido recibido(s)."'

/**
 * El bloque «Acuse de recibo» de la copia cedible, ya armado: recuadro, líneas
 * para Nombre, RUT, Fecha, Recinto y Firma, la leyenda de la ley y «CEDIBLE».
 * Son elementos sueltos, así que el cliente los mueve o cambia como cualquier otro.
 */
export function bloqueAcuseDeRecibo(x: number, y: number, def: Definicion): Elemento[] {
  const { ancho, alto } = dimensiones(def.paper)
  const w = 80
  const r = (v: number) => Math.round(v * 10) / 10
  const x0 = r(Math.min(x, ancho - w))
  const y0 = r(Math.min(y, (alto ?? 2000) - 44))
  const creados: Elemento[] = []
  const nuevo = (tipo: string, el: Omit<Elemento, 'id'>) => {
    const id = nuevoId(tipo, [...def.elements, ...creados])
    creados.push({ ...el, id, copy: 'cedible', pages: 'last' })
  }
  nuevo('acuse', { type: 'rect', x_mm: x0, y_mm: y0, w_mm: w, h_mm: 36, style: { border_mm: 0.35 } })
  for (const dy of [7, 14, 20, 26]) {
    nuevo('acuse-linea', { type: 'line', x_mm: x0, y_mm: y0 + dy, w_mm: w, h_mm: 0.3, style: { border_mm: 0.35 } })
  }
  nuevo('acuse-linea', { type: 'line', x_mm: x0 + 45, y_mm: y0, w_mm: 0.3, h_mm: 7, style: { border_mm: 0.35 } })
  const rotulo = (t: string, dx: number, dy: number, w2: number) =>
    nuevo('acuse-texto', { type: 'text', x_mm: x0 + dx, y_mm: y0 + dy, w_mm: w2, h_mm: 5, text: t,
      style: { font_size_pt: 8, valign: 'middle' } })
  rotulo('Fecha:', 1, 1, 43)
  rotulo('RUT:', 46, 1, 33)
  rotulo('Nombre:', 1, 8, 78)
  rotulo('Recinto:', 1, 14.5, 78)
  rotulo('Firma:', 1, 20.5, 78)
  nuevo('acuse-texto', { type: 'text', x_mm: x0 + 1, y_mm: y0 + 26.5, w_mm: w - 2, h_mm: 9, text: LEYENDA_ACUSE,
    style: { font_size_pt: 6, line_height: 1 } })
  nuevo('cedible', { type: 'text', x_mm: x0, y_mm: y0 + 37, w_mm: w, h_mm: 6, text: 'CEDIBLE',
    style: { font_size_pt: 12, bold: true, align: 'right' } })
  return creados.map(e => acotar(e, ancho, alto))
}

/** Un campo de la paleta soltado en el lienzo: un texto con ese campo. */
export function elementoDeCampo(ruta: string, x: number, y: number, def: Definicion): Elemento {
  const { ancho, alto } = dimensiones(def.paper)
  return acotar(
    { id: nuevoId('campo', def.elements), type: 'text', x_mm: x, y_mm: y, w_mm: 50, h_mm: 6, text: `{${ruta}}` },
    ancho, alto,
  )
}

export const TIENE_ALTO = (el: Elemento) => el.type !== 'detail_table'

// ─── Arrastre desde la paleta ─────────────────────────────────────────────

export const TIPO_DE_ARRASTRE = { campo: 'application/x-clobi-campo', elemento: 'application/x-clobi-elemento' }

/** Lo que viaja al soltar un campo: la ruta completa y, si va en una tabla, la relativa a su fila. */
export interface CampoArrastrado {
  ruta: string
  lista: string | null
  rutaEnLaLista: string
}

// ─── Imán a los bordes ──────────────────────────────────────────────────────

/** A cuántos mm de un borde ajeno el elemento se pega a él. */
export const UMBRAL_IMAN_MM = 1.5

export interface Guias {
  x: number[]
  y: number[]
}

/** La caja que el elemento ocupa en la vista: la tabla del detalle, según su zona. */
export function cajaEnVista(el: Elemento, vista: Vista): { x: number; y: number; w: number; h: number } {
  if (el.type === 'detail_table') {
    const { top, bottom } = zonaDelDetalle(el, vista)
    return { x: el.x_mm, y: top, w: el.w_mm, h: bottom - top }
  }
  return { x: el.x_mm, y: el.y_mm, w: el.w_mm, h: el.h_mm ?? 0 }
}

/**
 * Las líneas a las que se puede pegar un elemento: bordes y centros de los
 * demás elementos visibles en la vista, y bordes y centro de la página.
 */
export function lineasDeIman(def: Definicion, excepto: string | string[], vista: Vista): Guias {
  const { ancho } = dimensiones(def.paper)
  const alto = altoDelLienzo(def)
  const fuera = new Set(Array.isArray(excepto) ? excepto : [excepto])
  const x = [0, ancho / 2, ancho]
  const y = [0, alto / 2, alto]
  for (const el of def.elements) {
    if (fuera.has(el.id) || !visibleEnVista(el, vista)) continue
    const c = cajaEnVista(el, vista)
    x.push(c.x, c.x + c.w / 2, c.x + c.w)
    y.push(c.y, c.y + c.h / 2, c.y + c.h)
  }
  return { x, y }
}

/**
 * Pega un tramo [inicio, inicio + largo] a la línea más cercana por su
 * inicio, su centro o su final. Devuelve el inicio ajustado y la línea usada.
 */
export function pegarTramo(
  inicio: number, largo: number, lineas: number[], umbral = UMBRAL_IMAN_MM,
): { inicio: number; guia: number | null } {
  let mejor: { distancia: number; inicio: number; guia: number } | null = null
  for (const desplazamiento of [0, largo / 2, largo]) {
    for (const linea of lineas) {
      const distancia = Math.abs(inicio + desplazamiento - linea)
      if (distancia <= umbral && (!mejor || distancia < mejor.distancia)) {
        mejor = { distancia, inicio: linea - desplazamiento, guia: linea }
      }
    }
  }
  return mejor ? { inicio: Math.round(mejor.inicio * 10) / 10, guia: mejor.guia } : { inicio, guia: null }
}

/** Pega un borde suelto (al redimensionar) a la línea más cercana. */
export function pegarBorde(borde: number, lineas: number[], umbral = UMBRAL_IMAN_MM): { borde: number; guia: number | null } {
  let mejor: number | null = null
  for (const linea of lineas) {
    if (Math.abs(borde - linea) <= umbral && (mejor === null || Math.abs(borde - linea) < Math.abs(borde - mejor))) mejor = linea
  }
  return mejor === null ? { borde, guia: null } : { borde: mejor, guia: mejor }
}

// ─── Selección múltiple ─────────────────────────────────────────────────────

export interface Caja {
  x: number
  y: number
  w: number
  h: number
}

const decima = (v: number) => Math.round(v * 10) / 10

/** El elemento corrido (dx, dy) mm. La tabla del detalle corre sus dos zonas y su final. */
export function desplazar(el: Elemento, dx: number, dy: number): Elemento {
  const movido: Elemento = { ...el, x_mm: decima(el.x_mm + dx), y_mm: decima(el.y_mm + dy) }
  if (el.type === 'detail_table') {
    movido.y_next_mm = decima((el.y_next_mm ?? el.y_mm) + dy)
    movido.bottom_mm = decima((el.bottom_mm ?? el.y_mm + 20) + dy)
  }
  return movido
}

/** La caja que envuelve a los elementos en la vista. */
export function cajaDelGrupo(els: Elemento[], vista: Vista): Caja {
  const cajas = els.map(el => cajaEnVista(el, vista))
  const x = Math.min(...cajas.map(c => c.x))
  const y = Math.min(...cajas.map(c => c.y))
  return { x, y, w: Math.max(...cajas.map(c => c.x + c.w)) - x, h: Math.max(...cajas.map(c => c.y + c.h)) - y }
}

/**
 * El corrimiento que deja la caja dentro de la página: un grupo que choca con
 * el borde se detiene entero, en vez de que cada elemento se aplaste contra él.
 */
export function acotarCorrimiento(caja: Caja, dx: number, dy: number, ancho: number, alto: number | null) {
  const limiteY = alto ?? 2000
  return {
    dx: entre(dx, -caja.x, Math.max(-caja.x, ancho - caja.x - caja.w)),
    dy: entre(dy, -caja.y, Math.max(-caja.y, limiteY - caja.y - caja.h)),
  }
}

/** Los ids de los elementos que quedan completamente dentro del recuadro (en mm). */
export function dentroDelRecuadro(els: Elemento[], recuadro: Caja, vista: Vista): string[] {
  return els.filter(el => {
    const c = cajaEnVista(el, vista)
    return c.x >= recuadro.x && c.y >= recuadro.y
      && c.x + c.w <= recuadro.x + recuadro.w && c.y + c.h <= recuadro.y + recuadro.h
  }).map(el => el.id)
}

export type Alineacion = 'izquierda' | 'centro' | 'derecha' | 'arriba' | 'medio' | 'abajo'

/**
 * Alinea los elementos a la caja que los envuelve o, si es uno solo, a la
 * página (centrar «CEDIBLE» en la hoja). Devuelve los elementos movidos.
 */
export function alinear(els: Elemento[], como: Alineacion, vista: Vista, pagina: Caja): Elemento[] {
  if (!els.length) return []
  const ref = els.length === 1 ? pagina : cajaDelGrupo(els, vista)
  return els.map(el => {
    const c = cajaEnVista(el, vista)
    const destino = {
      izquierda: [ref.x, c.y], centro: [ref.x + (ref.w - c.w) / 2, c.y], derecha: [ref.x + ref.w - c.w, c.y],
      arriba: [c.x, ref.y], medio: [c.x, ref.y + (ref.h - c.h) / 2], abajo: [c.x, ref.y + ref.h - c.h],
    }[como]
    return desplazar(el, destino[0] - c.x, destino[1] - c.y)
  })
}

/**
 * Reparte los elementos con el mismo espacio entre uno y otro, sin mover el
 * primero ni el último del eje. Necesita 3 o más; con menos no hay qué repartir.
 */
export function distribuir(els: Elemento[], eje: 'x' | 'y', vista: Vista): Elemento[] {
  if (els.length < 3) return els
  const largo = eje === 'x' ? 'w' : 'h'
  const ordenados = els.map(el => ({ el, c: cajaEnVista(el, vista) })).sort((a, b) => a.c[eje] - b.c[eje])
  const grupo = cajaDelGrupo(els, vista)
  const ocupado = ordenados.reduce((s, o) => s + o.c[largo], 0)
  const espacio = (grupo[largo] - ocupado) / (ordenados.length - 1)
  let cursor = grupo[eje]
  const movidos = new Map<string, Elemento>()
  for (const { el, c } of ordenados) {
    const d = cursor - c[eje]
    movidos.set(el.id, eje === 'x' ? desplazar(el, d, 0) : desplazar(el, 0, d))
    cursor += c[largo] + espacio
  }
  return els.map(el => movidos.get(el.id)!)
}

/**
 * Si la medida se puede igualar: el alto de la tabla del detalle es su zona, el
 * de la lista de datos sale de sus filas, y el «alto» de una línea horizontal
 * (o el ancho de una vertical) es su grosor.
 */
export function admiteIgualar(el: Elemento, medida: 'w' | 'h'): boolean {
  const horizontal = el.w_mm >= (el.h_mm ?? 0)
  if (el.type === 'line') return medida === 'w' ? horizontal : !horizontal
  return medida === 'w' || (el.type !== 'detail_table' && el.type !== 'field_list')
}

/**
 * El ancho (o el alto) de todos igual al del más grande, como en PowerPoint: no
 * depende del orden en que se eligieron. Los que no admiten la medida quedan igual.
 */
export function igualarMedida(els: Elemento[], medida: 'w' | 'h'): Elemento[] {
  const campo = medida === 'w' ? 'w_mm' : 'h_mm'
  const aptos = els.filter(el => admiteIgualar(el, medida))
  if (aptos.length < 2) return els
  const mayor = Math.max(...aptos.map(el => el[campo] ?? 0))
  return els.map(el => (admiteIgualar(el, medida) ? { ...el, [campo]: mayor } : el))
}

// ─── Campos para las columnas de las tablas ─────────────────────────────────

export interface CampoDeLista {
  /** Relativa a la fila: `NmbItem`, `CdgItem/VlrCodigo`. */
  ruta: string
  nombre: string
  descripcion: string
}

interface NodoMinimo {
  name: string
  path: string
  description: string
  repeatable: boolean
  children?: NodoMinimo[]
}

/**
 * Las hojas bajo `Detalle` o `Referencia` de la paleta, con la ruta relativa a
 * la fila (lo que usa una columna). Los repetibles internos (CdgItem) toman su
 * primer elemento, como hace el motor con `find`.
 */
export function camposDeLaLista(arbol: NodoMinimo[], lista: 'Detalle' | 'Referencia'): CampoDeLista[] {
  const raiz = arbol.find(n => n.name === lista)
  if (!raiz?.children) return []
  const salida: CampoDeLista[] = []
  const recorrer = (nodos: NodoMinimo[], prefijo: string, etiqueta: string) => {
    for (const n of nodos) {
      const ruta = prefijo + n.path
      const nombre = etiqueta ? `${etiqueta} › ${n.name}` : n.name
      if (n.children) recorrer(n.children, n.repeatable ? `${ruta}/` : prefijo, nombre)
      else salida.push({ ruta, nombre, descripcion: n.description })
    }
  }
  recorrer(raiz.children, '', '')
  return salida
}

// ─── Cambio de papel u orientación ──────────────────────────────────────────

/**
 * La definición con otro papel, con cada elemento reacomodado en proporción al
 * tamaño nuevo. Sin esto, una carta vertical pasada a horizontal deja el
 * timbre y los totales fuera de la hoja y la plantilla no se puede publicar.
 * Entre papeles de alto automático (rollos) solo se escala el ancho.
 */
export function adaptarAlPapel(def: Definicion, papel: Papel): Definicion {
  const antes = dimensiones(def.paper)
  const despues = dimensiones(papel)
  const fx = despues.ancho / antes.ancho
  const fy = antes.alto !== null && despues.alto !== null ? despues.alto / antes.alto : 1
  const r = (v: number) => Math.round(v * 10) / 10
  const elements = def.elements.map(el => {
    const escalado: Elemento = { ...el, x_mm: r(el.x_mm * fx), w_mm: r(el.w_mm * fx), y_mm: r(el.y_mm * fy) }
    if (el.h_mm !== undefined) escalado.h_mm = r(el.h_mm * fy)
    if (el.y_next_mm !== undefined) escalado.y_next_mm = r(el.y_next_mm * fy)
    if (el.bottom_mm !== undefined) escalado.bottom_mm = r(el.bottom_mm * fy)
    return acotar(escalado, despues.ancho, despues.alto)
  })
  return { ...def, paper: papel, elements }
}

/** El texto del elemento «Monto en palabras» de la paleta: se calcula del total. */
export const TEXTO_MONTO_EN_PALABRAS = 'SON: {Encabezado/Totales/MntTotal|amount_in_words|upper} PESOS'

// ─── Tipos de documento y papeles ───────────────────────────────────────────

/** Los tipos que una plantilla puede declarar, agrupados por su esquema del SII. */
export const TIPOS_DTE: { code: number; label: string; grupo: string }[] = [
  { code: 33, label: 'Factura', grupo: 'Facturas, notas y guías' },
  { code: 34, label: 'Factura exenta', grupo: 'Facturas, notas y guías' },
  { code: 46, label: 'Factura de compra', grupo: 'Facturas, notas y guías' },
  { code: 52, label: 'Guía de despacho', grupo: 'Facturas, notas y guías' },
  { code: 56, label: 'Nota de débito', grupo: 'Facturas, notas y guías' },
  { code: 61, label: 'Nota de crédito', grupo: 'Facturas, notas y guías' },
  { code: 39, label: 'Boleta', grupo: 'Boletas' },
  { code: 41, label: 'Boleta exenta', grupo: 'Boletas' },
  { code: 110, label: 'Factura de exportación', grupo: 'Exportación' },
  { code: 111, label: 'Nota de débito de exportación', grupo: 'Exportación' },
  { code: 112, label: 'Nota de crédito de exportación', grupo: 'Exportación' },
  { code: 43, label: 'Liquidación factura', grupo: 'Liquidación' },
]
export const nombreTipo = (code: number) => TIPOS_DTE.find(t => t.code === code)?.label ?? String(code)

export const PAPELES = [
  { value: 'letter', label: 'Carta' },
  { value: 'legal', label: 'Oficio' },
  { value: 'half_letter', label: 'Media carta' },
  { value: 'a4', label: 'A4' },
  { value: 'roll_80', label: 'Rollo de 80 mm' },
  { value: 'roll_76', label: 'Rollo de 76 mm' },
  { value: 'roll_58', label: 'Rollo de 58 mm' },
  { value: 'custom', label: 'Personalizado' },
]
export const esRollo = (preset: string) => preset.startsWith('roll_')

export const vaEnLaCopia = (el: Elemento, copia: Copia) => !el.copy || el.copy === 'both' || el.copy === copia

/** Dos copias por hoja: solo en papeles de alto fijo. */
export const admiteDosPorHoja = (papel: Papel) => !esRollo(papel.preset) && !(papel.preset === 'custom' && papel.height_mm == null)

/** Las fracciones (0 a 100) donde van las líneas de la grilla: los bordes de cada columna. */
export function bordesDeColumnas(columnas: Columna[]): number[] {
  const bordes = [0]
  let acumulado = 0
  for (const c of columnas) {
    acumulado += c.width_pct || 0
    bordes.push(Math.min(acumulado, 100))
  }
  return bordes
}

/**
 * Las columnas que se dibujan con el documento de ejemplo (sus índices) y el %
 * que dejan libre las que no van: como en el PDF, cada columna conserva su
 * ancho y la tabla se angosta.
 */
export function columnasQueSeVen(columnas: Columna[], visibles?: boolean[]): { indices: number[]; libre: number } {
  const indices = columnas.map((_, i) => i).filter(i => visibles?.[i] !== false)
  if (indices.length === columnas.length) return { indices, libre: 0 }
  const suma = indices.reduce((s, i) => s + (columnas[i].width_pct || 0), 0)
  return { indices, libre: Math.max(0, 100 - suma) }
}

/** Alto que ocupan las referencias sobre el detalle (título, encabezado y una fila por referencia). */
export function altoDeReferencias(refs: ReferenciasDelDetalle | undefined, filas: number): number {
  if (!refs?.columns?.length || filas === 0) return 0
  return (filas + 1 + (refs.title ? 1 : 0)) * (refs.row_h_mm ?? 4.5) + (refs.gap_mm ?? 2)
}
